import { Box, Card, Chip, Tooltip } from '@mui/material';
import {
  Datagrid,
  FilterButton,
  FunctionField,
  List,
  TextField,
  SelectArrayInput,
  SelectInput,
  TopToolbar,
  useRecordContext,
  useTranslate,
} from 'react-admin';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { DashboardContent } from 'src/layouts/dashboard';
import ErrorPage403 from 'src/pages/error/403';
import { PermissionGuard } from 'src/routes/components';
import AddressField from '../../components/AddressField';
import IdField from '../../components/IdField';
import XCurrencyField from '../../components/XCurrencyField';
import XChainField from '../../components/XChainField';
import XTextField from '../../components/XTextField';
import XTextInput from '../../components/XTextInput';
import { Resources } from '../resource';
import FilterGroup from '../../components/FilterGroup';
import dayjs from 'dayjs';
import { chains } from 'src/assets/data';
import XNumberField from '../../components/XNumberField';
import XDateField from '../../components/XDateField';
import { EmptyContent } from 'src/components/empty-content';
import { XDateTimeInput } from '../../components/XDateTimeInput';

const StatusChoiceWithTooltip = ({ record }: { record: any }) => {
  const t = useTranslate();
  const tooltips: Record<string, string> = {
    new_user: 'module.userInfo.statusTooltip.new_user',
    active_user: 'module.userInfo.statusTooltip.active_user',
    dormant_user: 'module.userInfo.statusTooltip.dormant_user',
    at_risk_user: 'module.userInfo.statusTooltip.at_risk_user',
    lost_user: 'module.userInfo.statusTooltip.lost_user',
    anonymous: 'module.userInfo.statusTooltip.anonymous',
  };
  return (
    <Tooltip title={t(tooltips[record.id] || tooltips.anonymous)} arrow placement="right">
      <span>{record.name}</span>
    </Tooltip>
  );
}

const UserInfoList = () => {
  const t = useTranslate();

  const userFilters = [
    <FilterGroup alwaysOn p={0}>
      <XDateTimeInput
        source="from"
        label={t('module.userInfo.from')}
        minDate={dayjs('2000-01-01')}
        maxDate={dayjs()}
        type="from"
      />
      <XDateTimeInput
        source="to"
        label={t('module.userInfo.to')}
        minDate={dayjs('2000-01-01')}
        maxDate={dayjs()}
        type="to"
      />
    </FilterGroup>,
    <XTextInput alwaysOn source="loginBy" label={t('module.userInfo.loginBy')} />,
    <XTextInput alwaysOn source="wallet" label={t('module.userInfo.walletAddress')} />,
    <XTextInput source="id" label={t('module.userInfo.id')} />,
    <SelectInput
      source="status"
      label={t('module.userInfo.status')}
      emptyText="All"
      choices={[
        { id: 'new_user', name: 'New User' },
        { id: 'active_user', name: 'Active User' },
        { id: 'dormant_user', name: 'Dormant User' },
        { id: 'at_risk_user', name: 'At Risk User' },
        { id: 'lost_user', name: 'Lost User' },
        { id: 'anonymous', name: 'Anonymous' },
      ]}
      optionText={(record) => <StatusChoiceWithTooltip record={record} />}
      sx={{ marginBottom: '0' }}
    />,
    <SelectArrayInput
      source="chains"
      label={t('module.userInfo.chains')}
      choices={chains}
      sx={{ marginBottom: '0' }}
    />,
  ];

  const userListActions = (
    <TopToolbar>
      <FilterButton />
    </TopToolbar>
  );

  const UserExpand = () => {
    const record = useRecordContext();
    const wallets = record?.wallets || [];

    return (
      <Card sx={{ marginLeft: '-16px', marginRight: '-16px' }}>
        <Datagrid
          rowClick={false}
          data={wallets}
          bulkActionButtons={false}
          sx={{
            '& .RaDatagrid-thead': {
              display: 'none', // ← Ẩn header
            },
            '& .MuiTableCell-root': {
              whiteSpace: 'nowrap',
            },
          }}
        >
          <FunctionField render={() => <Box sx={{ width: '34px' }}></Box>} />
          <FunctionField render={() => <Box sx={{ width: '100px' }}></Box>} />
          <FunctionField render={() => <Box sx={{ width: '150px' }}></Box>} />
          <FunctionField render={() => <Box sx={{ width: '136px' }}></Box>} />
          <FunctionField
            render={(item: any) => (
              <AddressField sx={{ width: '180px' }} address={item.walletAddress} />
            )}
          />
          <FunctionField render={() => <Box sx={{ width: '150px' }}></Box>} />
          <FunctionField
            render={(item: any) => <XChainField sx={{ width: '80px' }} value={item.chainId} />}
          />
          <FunctionField
            source="totalBuyDex"
            label={t('module.userInfo.totalBuyDex')}
            render={(record: any) => (
              <XCurrencyField
                currency={{ symbol: '$', position: 'before' }}
                sx={{ width: '125px' }}
                value={record.totalBuyDex}
              />
            )}
          />
          <FunctionField
            source="totalBuyMeme"
            label={t('module.userInfo.totalBuyMeme')}
            render={(record: any) => (
              <XCurrencyField
                currency={{ symbol: '$', position: 'before' }}
                sx={{ width: '125px' }}
                value={record.totalBuyMeme}
              />
            )}
          />
          <FunctionField
            source="totalBuyPredict"
            label={t('module.userInfo.totalBuyPredict')}
            render={(record: any) => (
              <XCurrencyField
                currency={{ symbol: '$', position: 'before' }}
                sx={{ width: '135px' }}
                value={record.totalBuyPredict}
              />
            )}
          />
          <FunctionField
            source="totalSellDex"
            label={t('module.userInfo.totalSellDex')}
            render={(record: any) => (
              <XCurrencyField
                currency={{ symbol: '$', position: 'before' }}
                sx={{ width: '125px' }}
                value={record.totalSellDex}
              />
            )}
          />
          <FunctionField
            source="totalSellMeme"
            label={t('module.userInfo.totalSellMeme')}
            render={(record: any) => (
              <XCurrencyField
                currency={{ symbol: '$', position: 'before' }}
                sx={{ width: '125px' }}
                value={record.totalSellMeme}
              />
            )}
          />
          <FunctionField
            source="totalSellPredict"
            label={t('module.userInfo.totalSellPredict')}
            render={(record: any) => (
              <XCurrencyField
                currency={{ symbol: '$', position: 'before' }}
                sx={{ width: '135px' }}
                value={record.totalSellPredict}
              />
            )}
          />
          <FunctionField
            source="totalPnlDex"
            label={t('module.userInfo.totalPnlDex')}
            render={(record: any) => (
              <XCurrencyField
                colorize
                currency={{ symbol: '$', position: 'before' }}
                sx={{ width: '125px' }}
                value={record.totalPnlDex}
              />
            )}
          />
          <FunctionField
            source="totalPnlMeme"
            label={t('module.userInfo.totalPnlMeme')}
            render={(record: any) => (
              <XCurrencyField
                colorize
                currency={{ symbol: '$', position: 'before' }}
                sx={{ width: '125px' }}
                value={record.totalPnlMeme}
              />
            )}
          />
          <FunctionField
            source="totalPnlPredict"
            label={t('module.userInfo.totalPnlPredict')}
            render={(record: any) => (
              <XCurrencyField
                colorize
                currency={{ symbol: '$', position: 'before' }}
                sx={{ width: '135px' }}
                value={record.totalPnlPredict}
              />
            )}
          />
          <FunctionField
            source="balanceDex"
            label={t('module.userInfo.balanceDex')}
            render={(record: any) => (
              <XCurrencyField
                currency={{ symbol: '$', position: 'before' }}
                sx={{ width: '125px' }}
                value={record.balanceDex}
              />
            )}
          />
          <FunctionField
            source="balanceMeme"
            label={t('module.userInfo.balanceMeme')}
            render={(record: any) => (
              <XCurrencyField
                currency={{ symbol: '$', position: 'before' }}
                sx={{ width: '125px' }}
                value={record.balanceMeme}
              />
            )}
          />
          <FunctionField
            source="balancePredict"
            label={t('module.userInfo.balancePredict')}
            render={(record: any) => (
              <XCurrencyField
                currency={{ symbol: '$', position: 'before' }}
                sx={{ width: '135px' }}
                value={record.balancePredict}
              />
            )}
          />
          <FunctionField
            source="totalDepositDex"
            label={t('module.userInfo.totalDepositDex')}
            render={(record: any) => (
              <XCurrencyField
                currency={{ symbol: '$', position: 'before' }}
                sx={{ width: '125px' }}
                value={record.totalDepositDex}
              />
            )}
          />
          <FunctionField
            source="totalDepositMeme"
            label={t('module.userInfo.totalDepositMeme')}
            render={(record: any) => (
              <XCurrencyField
                currency={{ symbol: '$', position: 'before' }}
                sx={{ width: '125px' }}
                value={record.totalDepositMeme}
              />
            )}
          />
          <FunctionField
            source="totalDepositPredict"
            label={t('module.userInfo.totalDepositPredict')}
            render={(record: any) => (
              <XCurrencyField
                currency={{ symbol: '$', position: 'before' }}
                sx={{ width: '145px' }}
                value={record.totalDepositPredict}
              />
            )}
          />
          <FunctionField
            source="totalWithdrawDex"
            label={t('module.userInfo.totalWithdrawDex')}
            render={(record: any) => (
              <XCurrencyField
                currency={{ symbol: '$', position: 'before' }}
                sx={{ width: '125px' }}
                value={record.totalWithdrawDex}
              />
            )}
          />
          <FunctionField
            source="totalWithdrawMeme"
            label={t('module.userInfo.totalWithdrawMeme')}
            render={(record: any) => (
              <XCurrencyField
                currency={{ symbol: '$', position: 'before' }}
                sx={{ width: '135px' }}
                value={record.totalWithdrawMeme}
              />
            )}
          />
          <FunctionField
            source="totalWithdrawPredict"
            label={t('module.userInfo.totalWithdrawPredict')}
            render={(record: any) => (
              <XCurrencyField
                currency={{ symbol: '$', position: 'before' }}
                sx={{ width: '145px' }}
                value={record.totalWithdrawPredict}
              />
            )}
          />
          <FunctionField
            source="dexRevenue"
            label={t('module.userInfo.dexRevenue')}
            render={(record: any) => (
              <XCurrencyField
                currency={{ symbol: '$', position: 'before' }}
                sx={{ width: '125px' }}
                value={record.dexRevenue}
              />
            )}
          />
          <FunctionField
            source="memeRevenue"
            label={t('module.userInfo.memeRevenue')}
            render={(record: any) => (
              <XCurrencyField
                currency={{ symbol: '$', position: 'before' }}
                sx={{ width: '125px' }}
                value={record.memeRevenue}
              />
            )}
          />
          <FunctionField
            source="predictRevenue"
            label={t('module.userInfo.predictRevenue')}
            render={(record: any) => (
              <XCurrencyField
                currency={{ symbol: '$', position: 'before' }}
                sx={{ width: '135px' }}
                value={record.predictRevenue}
              />
            )}
          />
          <FunctionField source="status" render={() => <Box sx={{ width: '100px' }}></Box>} />
        </Datagrid>
      </Card>
    );
  };

  return (
    <PermissionGuard resource="user-info" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent>
        <CustomBreadcrumbs
          heading={t('module.userInfo.title')}
          links={[{ name: t('module.userInfo.title'), href: `${Resources.userInfo}` }]}
        />
        <Card sx={{ mt: 2 }}>
          <List
            perPage={10}
            resource={Resources.userInfo}
            exporter={false}
            actions={userListActions}
            filters={userFilters}
            empty={<EmptyContent />}
            sort={{ field: 'registerAt', order: 'DESC' }}
            className="user-info"
            sx={{
              '&.user-info .RaList-actions form': {
                alignItems: { xs: 'flex-end', md: 'stretch' },
                flexDirection: { xs: 'column', md: 'row' },
                flexWrap: 'wrap',
                gap: 2,
                padding: 2,
                display: 'flex',
              },
              '&.user-info .RaList-actions .MuiToolbar-dense': {
                height: '100%',
                alignItems: 'center',
                alignContent: 'center',
                padding: '4px',
              },
              '&.user-info form > .filter-field:first-child': {
                width: '100%',
              },
              '&.user-info form > [data-source="chains"]': {
                width: '100%',
                marginBottom: '-16px',
              },
            }}
          >
            <Box sx={{ overflow: 'auto' }}>
              <Datagrid
                rowClick="show"
                bulkActionButtons={false}
                expand={UserExpand}
                className="user-statistic"
                empty={<EmptyContent />}
                sx={{
                  '& .MuiTableCell-root': {
                    whiteSpace: 'nowrap',
                  },
                  // '& .RaDatagrid-tbody tr:hover .RaDatagrid-table .datagrid-body tr td': {
                  //   backgroundColor: 'var(--palette-background-neutral)'
                  // },
                  '&.user-statistic .RaDatagrid-expandedPanel td': {
                    color: 'var(--palette-text-secondary)',
                    borderColor: 'var(--palette-background-paper)',
                  },
                }}
              >
                <FunctionField
                  label={t('module.userInfo.id')}
                  render={(record: any) => (
                    <IdField id={record.id} emptyBox sx={{ width: '100px' }} />
                  )}
                />
                <FunctionField
                  label={t('module.userInfo.loginBy')}
                  render={(record: any) => (
                    <XTextField text={record.loginBy} emptyBox sx={{ width: '150px' }} />
                  )}
                />
                <FunctionField
                  source="registerAt"
                  label={t('module.userInfo.registerAt')}
                  render={(record: any) => (
                    <XDateField
                      fullDate
                      value={record.registerAt}
                      emptyBox
                      sx={{ width: '150px' }}
                    />
                  )}
                />

                <TextField source="registrationCountry" label={t('module.userInfo.registrationCountry')} sortable={false} />
                <FunctionField
                  label={t('module.userInfo.wallets')}
                  render={(record: any) => (
                    <XNumberField number={record.wallets.length} sx={{ width: '180px' }} />
                  )}
                />
                <FunctionField
                  label={t('module.userInfo.chains')}
                  render={(record: any) => (
                    <XChainField sx={{ width: '80px' }} value={record.chainId} />
                  )}
                />
                <FunctionField
                  source="totalBuyDex"
                  label={t('module.userInfo.totalBuyDex')}
                  render={(record: any) => (
                    <XCurrencyField
                      currency={{ symbol: '$', position: 'before' }}
                      sx={{ width: '125px' }}
                      value={record.totalBuyDex}
                    />
                  )}
                />
                <FunctionField
                  source="totalBuyMeme"
                  label={t('module.userInfo.totalBuyMeme')}
                  render={(record: any) => (
                    <XCurrencyField
                      currency={{ symbol: '$', position: 'before' }}
                      sx={{ width: '125px' }}
                      value={record.totalBuyMeme}
                    />
                  )}
                />
                <FunctionField
                  source="totalBuyPredict"
                  label={t('module.userInfo.totalBuyPredict')}
                  render={(record: any) => (
                    <XCurrencyField
                      currency={{ symbol: '$', position: 'before' }}
                      sx={{ width: '135px' }}
                      value={record.totalBuyPredict}
                    />
                  )}
                />
                <FunctionField
                  source="totalSellDex"
                  label={t('module.userInfo.totalSellDex')}
                  render={(record: any) => (
                    <XCurrencyField
                      currency={{ symbol: '$', position: 'before' }}
                      sx={{ width: '125px' }}
                      value={record.totalSellDex}
                    />
                  )}
                />
                <FunctionField
                  source="totalSellMeme"
                  label={t('module.userInfo.totalSellMeme')}
                  render={(record: any) => (
                    <XCurrencyField
                      currency={{ symbol: '$', position: 'before' }}
                      sx={{ width: '125px' }}
                      value={record.totalSellMeme}
                    />
                  )}
                />
                <FunctionField
                  source="totalSellPredict"
                  label={t('module.userInfo.totalSellPredict')}
                  render={(record: any) => (
                    <XCurrencyField
                      currency={{ symbol: '$', position: 'before' }}
                      sx={{ width: '135px' }}
                      value={record.totalSellPredict}
                    />
                  )}
                />
                <FunctionField
                  source="totalPnlDex"
                  label={t('module.userInfo.totalPnlDex')}
                  render={(record: any) => (
                    <XCurrencyField
                      colorize
                      currency={{ symbol: '$', position: 'before' }}
                      sx={{ width: '125px' }}
                      value={record.totalPnlDex}
                    />
                  )}
                />
                <FunctionField
                  source="totalPnlMeme"
                  label={t('module.userInfo.totalPnlMeme')}
                  render={(record: any) => (
                    <XCurrencyField
                      colorize
                      currency={{ symbol: '$', position: 'before' }}
                      sx={{ width: '125px' }}
                      value={record.totalPnlMeme}
                    />
                  )}
                />
                <FunctionField
                  source="totalPnlPredict"
                  label={t('module.userInfo.totalPnlPredict')}
                  render={(record: any) => (
                    <XCurrencyField
                      colorize
                      currency={{ symbol: '$', position: 'before' }}
                      sx={{ width: '135px' }}
                      value={record.totalPnlPredict}
                    />
                  )}
                />
                <FunctionField
                  source="balanceDex"
                  label={t('module.userInfo.balanceDex')}
                  render={(record: any) => (
                    <XCurrencyField
                      currency={{ symbol: '$', position: 'before' }}
                      sx={{ width: '125px' }}
                      value={record.balanceDex}
                    />
                  )}
                />
                <FunctionField
                  source="balanceMeme"
                  label={t('module.userInfo.balanceMeme')}
                  render={(record: any) => (
                    <XCurrencyField
                      currency={{ symbol: '$', position: 'before' }}
                      sx={{ width: '125px' }}
                      value={record.balanceMeme}
                    />
                  )}
                />
                <FunctionField
                  source="balancePredict"
                  label={t('module.userInfo.balancePredict')}
                  render={(record: any) => (
                    <XCurrencyField
                      currency={{ symbol: '$', position: 'before' }}
                      sx={{ width: '135px' }}
                      value={record.balancePredict}
                    />
                  )}
                />
                <FunctionField
                  source="totalDepositDex"
                  label={t('module.userInfo.totalDepositDex')}
                  render={(record: any) => (
                    <XCurrencyField
                      currency={{ symbol: '$', position: 'before' }}
                      sx={{ width: '125px' }}
                      value={record.totalDepositDex}
                    />
                  )}
                />
                <FunctionField
                  source="totalDepositMeme"
                  label={t('module.userInfo.totalDepositMeme')}
                  render={(record: any) => (
                    <XCurrencyField
                      currency={{ symbol: '$', position: 'before' }}
                      sx={{ width: '125px' }}
                      value={record.totalDepositMeme}
                    />
                  )}
                />
                <FunctionField
                  source="totalDepositPredict"
                  label={t('module.userInfo.totalDepositPredict')}
                  render={(record: any) => (
                    <XCurrencyField
                      currency={{ symbol: '$', position: 'before' }}
                      sx={{ width: '145px' }}
                      value={record.totalDepositPredict}
                    />
                  )}
                />
                <FunctionField
                  source="totalWithdrawDex"
                  label={t('module.userInfo.totalWithdrawDex')}
                  render={(record: any) => (
                    <XCurrencyField
                      currency={{ symbol: '$', position: 'before' }}
                      sx={{ width: '125px' }}
                      value={record.totalWithdrawDex}
                    />
                  )}
                />
                <FunctionField
                  source="totalWithdrawMeme"
                  label={t('module.userInfo.totalWithdrawMeme')}
                  render={(record: any) => (
                    <XCurrencyField
                      currency={{ symbol: '$', position: 'before' }}
                      sx={{ width: '135px' }}
                      value={record.totalWithdrawMeme}
                    />
                  )}
                />
                <FunctionField
                  source="totalWithdrawPredict"
                  label={t('module.userInfo.totalWithdrawPredict')}
                  render={(record: any) => (
                    <XCurrencyField
                      currency={{ symbol: '$', position: 'before' }}
                      sx={{ width: '145px' }}
                      value={record.totalWithdrawPredict}
                    />
                  )}
                />
                <FunctionField
                  source="dexRevenue"
                  label={t('module.userInfo.dexRevenue')}
                  render={(record: any) => (
                    <XCurrencyField
                      currency={{ symbol: '$', position: 'before' }}
                      sx={{ width: '125px' }}
                      value={record.dexRevenue}
                    />
                  )}
                />
                <FunctionField
                  source="memeRevenue"
                  label={t('module.userInfo.memeRevenue')}
                  render={(record: any) => (
                    <XCurrencyField
                      currency={{ symbol: '$', position: 'before' }}
                      sx={{ width: '125px' }}
                      value={record.memeRevenue}
                    />
                  )}
                />
                <FunctionField
                  source="predictRevenue"
                  label={t('module.userInfo.predictRevenue')}
                  render={(record: any) => (
                    <XCurrencyField
                      currency={{ symbol: '$', position: 'before' }}
                      sx={{ width: '135px' }}
                      value={record.predictRevenue}
                    />
                  )}
                />
                <FunctionField
                  source="status"
                  label={t('module.userInfo.status')}
                  render={(record) => {
                    let color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' = 'default';
                    const statusLabels: Record<string, string> = {
                      new_user: 'New User',
                      active_user: 'Active User',
                      dormant_user: 'Dormant User',
                      at_risk_user: 'At Risk User',
                      lost_user: 'Lost User',
                      anonymous: 'Anonymous',
                    };

                    switch (record.status) {
                      case 'active_user':
                        color = 'success';
                        break;
                      case 'new_user':
                        color = 'info';
                        break;
                      case 'dormant_user':
                      case 'at_risk_user':
                        color = 'warning';
                        break;
                      case 'lost_user':
                        color = 'error';
                        break;
                      case 'anonymous':
                      default:
                        color = 'default';
                        break;
                    }
                    return (
                      <Tooltip
                        title={t(`module.userInfo.statusTooltip.${record.status}`, { _: t('module.userInfo.statusTooltip.anonymous') })}
                        arrow
                      >
                        <Chip
                          sx={{ width: '100px' }}
                          label={statusLabels[record.status] || 'Anonymous'}
                          color={color}
                          size="small"
                        />
                      </Tooltip>

                    );
                  }}
                />
              </Datagrid>
            </Box>
          </List>
        </Card>
      </DashboardContent>
    </PermissionGuard>
  );
};

export default UserInfoList;
